import React from 'react';
import { Box } from '@mui/material';
import { BrandLockup } from './BrandLockup';
import { RotatingHero, HeroDots, useRotatingHero } from './hero';
import {
  HERO_GRADIENT,
  HERO_RADIAL_ACCENT,
  HERO_WHITE,
  HERO_TEXT_META,
  HERO_FONT_MONO,
} from './designTokens';

/** Subtle grid + curve SVG overlay — purely decorative. */
function DataPanelDeco() {
  return (
    <Box
      component="svg"
      viewBox="0 0 320 320"
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity: 0.22,
        pointerEvents: 'none',
      }}
    >
      {Array.from({ length: 11 }).map((_, i) => (
        <line key={`v${i}`} x1={i * 32} y1={0} x2={i * 32} y2={320} stroke={HERO_WHITE} strokeOpacity={0.05} strokeWidth={1} />
      ))}
      {Array.from({ length: 11 }).map((_, i) => (
        <line key={`h${i}`} x1={0} y1={i * 32} x2={320} y2={i * 32} stroke={HERO_WHITE} strokeOpacity={0.05} strokeWidth={1} />
      ))}
      <defs>
        <linearGradient id="pp-hero-ld1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={HERO_WHITE} stopOpacity={0.4} />
          <stop offset="100%" stopColor={HERO_WHITE} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d="M0 220 Q60 200 100 170 T180 130 T260 90 T320 60" stroke="url(#pp-hero-ld1)" strokeWidth={1.5} fill="none" />
      <path d="M0 250 Q60 240 100 210 T180 180 T260 150 T320 130" stroke={HERO_WHITE} strokeOpacity={0.18} strokeWidth={1} fill="none" />
    </Box>
  );
}

/**
 * Dark hero panel (left column of the sign-in split layout).
 * Renders a randomly-selected LOGIN_PIECE on mount; dot indicators step through all five.
 */
export function HeroPanel() {
  const { pieceIdx, setPieceIdx } = useRotatingHero();

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        minHeight: '100vh',
        background: HERO_GRADIENT,
        color: HERO_WHITE,
        px: '56px',
        py: '40px',
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
      }}
    >
      <DataPanelDeco />

      <Box
        aria-hidden
        sx={{ position: 'absolute', inset: 0, background: HERO_RADIAL_ACCENT, pointerEvents: 'none' }}
      />

      {/* Top — brand (decorative on the hero panel) */}
      <Box aria-hidden="true" sx={{ position: 'relative' }}>
        <BrandLockup size={40} dark />
      </Box>

      {/* Middle — rotating hero piece (decorative marketing content) */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: 0,
        }}
      >
        <RotatingHero pieceIdx={pieceIdx} />
      </Box>

      {/* Bottom — footer with dot indicators */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '0.71875rem',
          color: HERO_TEXT_META,
          fontFamily: HERO_FONT_MONO,
          letterSpacing: '0.04em',
        }}
      >
        <Box component="span" aria-hidden="true">© Teamlane 2026</Box>
        <HeroDots pieceIdx={pieceIdx} onSelect={setPieceIdx} />
      </Box>
    </Box>
  );
}
